export const LIBRARY_CONTENT = {
  TITLE: 'Library',
  SUBTITLE:
    'Access our collection of market insights, factsheets and presentations to stay informed about the latest developments in financial markets and at MarQ Capital',
  SECTIONS: {
    STAY_UP_TO_DATE: {
      TITLE: 'Stay Up to Date',
      TEXT: [
        'Stay informed with the latest developments at MarQ Capital. Our news section provides timely updates on our quantitative strategies, strategic milestones, product launches, partnerships and industry recognitions.',
        'Here, we share relevant announcements about the evolution of our business, including performance highlights, enhancements to our proprietary systems, and updates to our trading infrastructure.'
      ],
      HIGHLIGHT:
        'This space is designed to keep our clients and partners aligned with our vision, our ongoing progress, and everything that shapes the future of MarQ Capital.',
      NEWS_UPDATES: [
        {
          TITLE: 'Dravon Officially Launched',
          DATE: 'December 8, 2025',
          CONTENT:
            "We are pleased to announce the official launch of Dravon, which went live on December 8. Developed within MarQ Capital's quantitative research framework, Dravon is a systematic strategy designed to deliver targeted coverage in Gold.\n\nDravon integrates systematic exposure logic with adaptive market mechanisms and robust risk-management rules, creating a return profile engineered to complement multi-asset portfolios.\n\nHighlights from the validation phase:\n\n• Strategic coverage: Gold\n• Deep market adaptability through dynamic exposure logic\n• Robust risk controls supported by liquidity-aware stop mechanisms\n\nDravon's launch reinforces MarQ Capital's commitment to institutional-grade quantitative solutions: robust, adaptive and risk-aware, aimed at enhancing the resilience of multi-asset portfolios.\n\nFor further information and access to programme documentation, please visit: https://www.marqcapital.co.uk/strategies/dravon#hero",
          LINK: 'https://www.marqcapital.co.uk/strategies/dravon#hero'
        },
        {
          TITLE: '🚀 Merin Strategy Officially Live',
          DATE: 'November 10, 2025',
          CONTENT:
            "We are pleased to announce that Merin has successfully completed its incubation phase and is now officially live.\n\nBuilt within MarQ Capital's quantitative research framework, Merin is a systematic macro overlay strategy implemented exclusively through G7 FX markets. Its architecture combines adaptive algorithms, multi-layer signal processing, and strict risk-management logic to deliver a stable, uncorrelated return profile.\n\nThroughout incubation, Merin demonstrated:\n\n• Low volatility across intraday cycles\n• Strong defensive behaviour during periods of market stress\n• Very low correlation with major global equity indices\n• Efficient exposure control, thanks to liquidity-based stop logic\n\nMerin's live deployment marks an important milestone in expanding our suite of institutional-grade quantitative solutions. It reinforces our commitment to developing robust, adaptive, and risk-aware strategies capable of enhancing multi-asset portfolios.\n\nInvestors and allocators seeking diversification benefits through uncorrelated FX-based alpha may now request further information and access to the full programme documentation."
        },
        {
          TITLE: 'New Launch: Merin and Dravon Signal Programmes',
          DATE: 'June 2025',
          CONTENT:
            'We are pleased to announce the launch of **two new systematic strategies**, currently in the final stages of incubation. These additions further expand our technological and strategic footprint across global markets. \n\n{{MERIN_LOGO}}\n**The Merin** programme is focused on the **systematic trading of the seven major global currencies**, building on the success and methodology of our flagship Strand Q model. It leverages adaptive algorithms, advanced statistical analysis and machine learning techniques to dynamically adjust to macroeconomic developments and evolving market conditions within the G7 FX space. \n\n{{DRAVON_LOGO}} \n**Dravon** is designed to **capture macro and behavioural sentiment across multiple markets**, through a portfolio composed of **Crude Oil, Gold, the SPX, and EURUSD**. This approach provides a broad read of economic cycles, exploiting sentiment-driven inefficiencies across asset classes. The system actively manages exposure with strong risk sensitivity, aiming to protect capital during adverse conditions while optimising risk-adjusted returns. \nThese two new programmes reflect our ongoing commitment to innovation in systematic finance: **merging advanced technology with strategic insight** to deliver robust, adaptive and performance-driven signal solutions. \nPreliminary results will soon be made available to qualified investors seeking to diversify through next-generation systematic strategies.'
        },
        {
          TITLE: 'OctopusSync',
          DATE: 'March 2025',
          CONTENT:
            'Currently in development by Spinnaker Algo, a technology company within the MarQ Capital group, Octopus is a proprietary trading software designed to unite distinct trading platforms into a single, seamless infrastructure. It will offer multi-platform synchronization, real-time trade replication, advanced risk control, and scalable flexibility empowering traders and institutions to overcome the limitations of traditional systems.\n\nBuilt for precision and operational efficiency, Octopus will allow users to monitor and execute trades across multiple accounts simultaneously from a unified dashboard. The goal: reduce operational complexity and support disciplined, dynamic decision-making in real time.',
          LINK: 'https://www.octopussync.com'
        },
        {
          TITLE: 'New Brand',
          DATE: 'February 2025',
          CONTENT:
            "After its founding in 2022 and subsequent expansion into new markets, SpinnakerFX grew both externally and internally. The need emerged to create a new brand one that unites the company's multiple strengths: the creation and development of disruptive, successful technologies, alongside a robust quantitative strategy platform.\n\nThe world keeps moving, and so does MarQ Capital constantly innovating to stay ahead and deliver excellence across all areas of investment and technology."
        },
        {
          TITLE: 'SwingVision System',
          DATE: 'December 2023',
          CONTENT:
            'Already in live use within the group, this proprietary trading software owned and developed by Spinnaker Algo, part of the MarQ Capital Group powers Strand Q execution for EURUSD strategies with institutional-grade precision. It offers advanced risk control, latency-sensitive execution, and seamless integration across multiple trading systems.\n\nBuilt to perform under real market conditions, the platform ensures stability, traceability, and operational clarity. Designed for consistency and responsiveness, it enables MarQ Capital to trade with confidence even in high-volatility environments.'
        },
        {
          TITLE: 'From Code to Capital: The MarQ Capital Story',
          DATE: 'November 2022',
          CONTENT:
            'Founded in 2022, MarQ Capital is a quantitative strategy provider firm focused on long-term financial security and sustainable growth. More than a portfolio manager, we are a multicultural team combining market insight, data-driven strategies and a passion for finance delivering efficient, risk-conscious quantitatives solutions rooted in responsibility, innovation and excellence.\n\nOur journey began in 1987, when Luís Marques founded an IT company delivering customized solutions across industries. After decades in tech, he turned his focus to finance, beginning in 2008 a study that led to the creation of SwingVision. Developed from 2016, this system brought together programming expertise with self-learning models, going live in 2019 after three years of full-time work and further refinement through neural networks and decision tools.\n\nToday, MarQ Capital stands at the intersection of technology and finance, using proprietary innovations like SwingVision to navigate evolving markets. With a commitment to delivering clarity, stability and performance, we continue to build solutions that transform opportunities into lasting achievements for our clients.'
        }
      ]
    },
    FACTSHEET_DESCRIPTION: {
      TITLE: 'Strategy Description',
      TEXT: [
        'Explore our curated collection of presentations designed to communicate who we are and what we offer. This section includes institutional and product-specific materials that provide comprehensive information about our strategy approach.',
        'Each document is crafted to present key information in a visual and accessible format from overviews of our quantitative strategies to detailed breakdowns of performance, structure and risk management methodologies.',
        "These presentations are tailored for prospective investors and partners seeking to understand our approach, our capabilities and the value we aim to deliver. Whether you're interested in specific strategies or a broader understanding of our company, these resources provide valuable insights."
      ],
      STRATEGY_FACTSHEETS: [
        {
          TITLE: 'Strand Q',
          DESCRIPTION: 'Strategy Description',
          PDF_ID: 'strategy-factsheet-strand-q.pdf'
        },
        {
          TITLE: 'Dravon',
          DESCRIPTION: 'Strategy Description',
          PDF_ID: 'strategy-factsheet-dravon.pdf'
        },
        {
          TITLE: 'Merin',
          DESCRIPTION: 'Strategy Description',
          PDF_ID: 'strategy-factsheet-merin.pdf'
        }
      ]
    },
    FACTSHEETS: {
      TITLE: 'Factsheets',
      TEXT: [
        'Gain access to essential reports and structured insights that offer a clear view into our quantitative strategies and market perspectives. At MarQ Capital, we are committed to sharing high-quality analysis that supports informed decision-making.'
      ],
      LIST_ITEMS: [
        {
          TEXT: 'Monthly Factsheets for each of our signal programs, providing performance summaries, exposure analysis, and key risk metrics.'
        }
      ],
      MONTHLY_FACTSHEETS: {
        'Strand Q': [
          {
            TITLE: 'February 2026',
            DESCRIPTION: 'Strand Q Program',
            PDF_ID: 'file-202602-factsheet-strand-q.pdf'
          },
          {
            TITLE: 'January 2026',
            DESCRIPTION: 'Strand Q Program',
            PDF_ID: 'file-202601-factsheet-strand-q.pdf'
          },
          {
            TITLE: 'December 2025',
            DESCRIPTION: 'Strand Q Program',
            PDF_ID: 'file-202512-factsheet-strand-q.pdf'
          }
        ],
        Merin: [
          {
            TITLE: 'February 2026',
            DESCRIPTION: 'Merin Program',
            PDF_ID: 'file-202602-factsheet-merin.pdf'
          },
          {
            TITLE: 'January 2026',
            DESCRIPTION: 'Merin Program',
            PDF_ID: 'file-202601-factsheet-merin.pdf'
          },
          {
            TITLE: 'December 2025',
            DESCRIPTION: 'Merin Program',
            PDF_ID: 'file-202512-factsheet-merin.pdf'
          }
        ],
        Dravon: [
          {
            TITLE: 'February 2026',
            DESCRIPTION: 'Dravon Program',
            PDF_ID: 'file-202602-factsheet-dravon.pdf'
          },
          {
            TITLE: 'January 2026',
            DESCRIPTION: 'Dravon Program',
            PDF_ID: 'file-202601-factsheet-dravon.pdf'
          },
          {
            TITLE: 'December 2025',
            DESCRIPTION: 'Dravon Program',
            PDF_ID: 'file-202512-factsheet-dravon.pdf'
          }
        ]
      }
    },
    INSIGHTS: {
      TITLE: 'Insights',
      TEXT: [
        'Explore our regular market analysis and strategic commentary. Our insights combine technical analysis with macroeconomic perspectives to help you understand current market dynamics.'
      ],
      LIST_ITEMS: [
        {
          TEXT: 'Week 10 - Market Insights, offering concise commentary on relevant market movements, macroeconomic developments, and tactical positioning.'
        },
        {
          TEXT: 'Monthly Market Insight, which deliver a broader analysis of current global economic and trends in FX, WTI, Gold, US and EU stock markets.'
        }
      ],
      WEEKLY_MARKET_INSIGHTS: [
        {
          TITLE: 'U.S. Dollar Index',
          DESCRIPTION:
            'Rejected below 97.85 under dynamic average; targets 96.50, close above 97.85 neutralizes.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'U.S. Dollar Index - Weekly Market Insight',
          MODAL_CONTENT:
            'DXY Price remains in a broader downtrend and is being rejected below the 97.85 resistance, trading under the dynamic average. Structure continues to show lower highs. While below 97.85, bias favors a move toward 96.50. A daily close above 97.85 would be required to stabilize the short-term bearish pressure.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/cupgkkzu0pyhkcf71t7np/W10_usdindex.png?rlkey=6v0ydji3ttgpk3x0xg6dr75l6&st=47w68m0b&raw=1'
        },
        {
          TITLE: 'EURUSD',
          DESCRIPTION:
            'Ascending channel above 1.17; targets 1.2030, break below 1.17 opens 1.14–1.15.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'EURUSD - Weekly Market Insight',
          MODAL_CONTENT:
            'EURUSD Price remains in a well-defined ascending channel, holding above the rising dynamic average and printing higher lows. The recent consolidation near 1.18 appears constructive rather than distributive. While above 1.17, the bias favors continuation toward 1.2030 (channel resistance). A daily close below 1.17 would signal a deeper correction toward the 1.14–1.15 support zone, but the broader structure remains bullish.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/a5i1begrb0w4dbfx06ccm/1-W10_eur_usd.png?rlkey=xu15rjz84cior0ry4o89mcyo9&st=h9mmkhap&raw=1'
        },
        {
          TITLE: 'GBPUSD',
          DESCRIPTION:
            'Corrective pullback, higher lows above 1.3380; targets 1.3860, break below 1.3380 opens 1.30.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'GBPUSD - Weekly Market Insight',
          MODAL_CONTENT:
            'GBPUSD Price is holding above the rising dynamic average after a sharp rejection from the 1.38–1.39 resistance zone. The pullback looks corrective, with structure still printing higher lows above 1.3380 support. While above 1.3380, the bias favors another push toward 1.3860. A daily close below 1.3380 would shift the short-term structure and open room for a deeper retracement toward 1.30.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/649jkm7odfmvgtqkgguzp/2-W10_bp_usd.png?rlkey=qwwv9ea6a5cunqwfe30lr4dt7&st=3mala8k9&raw=1'
        },
        {
          TITLE: 'USDJPY',
          DESCRIPTION:
            'Correcting below 158.30 around MA with lower highs; targets 150.50, break above 158.30 restores bulls.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'USDJPY - Weekly Market Insight',
          MODAL_CONTENT:
            'USDJPY Price remains in a broader uptrend but is currently correcting after failing below the 158.30 resistance. The pair is trading around the dynamic average, with short-term structure showing lower highs. While below 158.30, downside risk favors a pullback toward 150.50 (trendline support). A daily close back above 158.30 would restore bullish momentum and reopen the path toward the 161.50 region.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/wu6dwvfzcyfjpw6c40cl2/3-W10_usd_jp.png?rlkey=22k3v8vh2jufk9zxbzts6l92u&st=3l54zdi3&raw=1'
        },
        {
          TITLE: 'USDCHF',
          DESCRIPTION:
            'Descending channel rejected below 0.7760; targets 0.7600/0.7550, close above 0.7760 neutralizes.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'USDCHF - Weekly Market Insight',
          MODAL_CONTENT:
            'USDCHF Price remains inside a broader descending channel and has been rejected below the 0.7760 resistance area. Structure continues to print lower highs, keeping downside pressure intact. While below 0.7760, bias favors a move toward 0.7600/0.7550. A daily close above 0.7760 would be needed to neutralize the immediate bearish structure.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/9rp45u6fxtq2wcb4paamo/4-W10_usd_swiss.png?rlkey=dasavndle7zrmtc7gtrlerctz&st=vq2mwtud&raw=1'
        },
        {
          TITLE: 'USDCAD',
          DESCRIPTION:
            'Descending channel below 1.3700; targets 1.3540, close above 1.3700 eases bearish momentum.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'USDCAD - Weekly Market Insight',
          MODAL_CONTENT:
            'USDCAD Price remains inside a descending channel, with the recent rebound failing below the dynamic average and prior structure resistance. Lower highs are intact, keeping short-term pressure to the downside. While below 1.3700, bias favors continuation toward 1.3540. A daily close back above 1.3700 would be needed to ease immediate bearish momentum.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/d9cr17xsqitjeozr0tvvg/5-W10_usd_cad.png?rlkey=a1xiczgamd3rjs7o2nbxrw2lu&st=21gu8qcr&raw=1'
        },
        {
          TITLE: 'AUDUSD',
          DESCRIPTION:
            'Strong impulsive breakout; above 0.7050 targets 0.7245, close below 0.7050 signals pullback.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'AUDUSD - Weekly Market Insight',
          MODAL_CONTENT:
            'AUDUSD Strong impulsive breakout above prior range resistance, with price holding firmly above the rising dynamic average. Structure is clearly bullish, printing higher highs and higher lows. While above 0.7050, bias favors continuation toward 0.7245. A daily close back below 0.7050 would signal a deeper corrective pullback.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/qqu0wurxt1agyp81juuv8/6-W10_aud_usd.png?rlkey=wola72qhwkpicuk06h89rj501&st=1ygwzk6u&raw=1'
        },
        {
          TITLE: 'NZDUSD',
          DESCRIPTION:
            'Rebounded from 0.5525; above 0.5900 targets 0.6050/0.6100, break below signals loss of momentum.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'NZDUSD - Weekly Market Insight',
          MODAL_CONTENT:
            'NZDUSD Price has rebounded strongly from the 0.5525 base and is holding above the rising dynamic average, maintaining a short-term bullish structure. The pullback looks corrective after the impulsive leg higher. While above 0.5900, bias favors continuation toward 0.6050/0.6100. A daily close back below 0.5900 would signal loss of upside momentum.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/te1jr0ze5rzfq8nf0388s/7-W10_nz_usd.png?rlkey=a5pucs3tcnxyp26hugdc59o9w&st=7pzjutz3&raw=1'
        },
        {
          TITLE: 'WTI Crude',
          DESCRIPTION:
            'Rebounding from 58.80; above 64.00 targets 70.30 resistance, rejection below 64.00 re-opens 58.80.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'WTI Crude - Weekly Market Insight',
          MODAL_CONTENT:
            'WTI Price is rebounding from the 58.80 support and trading back above the rising dynamic average, signaling short-term momentum shift within the broader channel. The structure suggests a corrective recovery phase. While above 64.00, bias favors continuation toward 70.30 resistance. A rejection below 64.00 would expose a retest of the 58.80 base.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/x44nbbfk1myqu2kdyp3zc/W10_crude.png?rlkey=yfy7wd9nt9ecjjpuiiboqnpwc&st=yyd8oc12&raw=1'
        },
        {
          TITLE: 'GOLD',
          DESCRIPTION:
            'Uptrend pressing 5,560 resistance; above 5,135 targets 5,560, failure allows corrective pullback.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'GOLD - Weekly Market Insight',
          MODAL_CONTENT:
            'Gold Strong structural uptrend remains intact, with price pressing into the 5,560 resistance zone after a clean impulsive leg higher. Momentum is elevated but not yet decisively reversing. While above 5,135, bias stays bullish toward 5,560. Failure there opens room for a corrective pullback before trend continuation.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/5uhh99aq3h397oji0pcj2/W10_gold.png?rlkey=eq1p65qsuedwlnop1zm14z78j&st=7aljqa1w&raw=1'
        },
        {
          TITLE: 'SP500',
          DESCRIPTION:
            'Consolidating near highs below 7,047; above MA, pullback toward 6,551 unless break above.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'SP500 - Weekly Market Insight',
          MODAL_CONTENT:
            'S&P 500 Price is consolidating near highs within a short-term descending channel, holding above the rising dynamic average. The structure remains broadly bullish, but momentum is compressing below 7,047 resistance. While below 7,047, risk favors a pullback toward 6,551. A clean daily breakout above 7,047 would resume the primary uptrend.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/8cx332bx8higipmbi9pq2/W10_sp500.png?rlkey=sr57eros1kej12stoka9a6pn8&st=n6pqtzap&raw=1'
        },
        {
          TITLE: 'EuroStoxx 50',
          DESCRIPTION:
            'Reached 6,200 resistance; below 6,200 pullback to 5,850, break above confirms continuation.',
          IS_PDF: false,
          HAS_MODAL: true,
          MODAL_TITLE: 'EuroStoxx 50 - Weekly Market Insight',
          MODAL_CONTENT:
            'EuroStoxx 50 Price has reached the 6,200 resistance zone and is showing signs of rejection after an extended bullish leg. Momentum is stretched, with price trading well above the rising dynamic average. While below 6,200, risk favors a corrective pullback toward 5,850. A clean breakout above 6,200 would confirm continuation of the primary uptrend.',
          MODAL_IMAGE:
            'https://www.dropbox.com/scl/fi/vfokp9mpukm1oe5j3zm58/W10_eurostoxx50.png?rlkey=l4f71dwh9pwc85j6wccy2o68t&st=8fsw9ahn&raw=1'
        }
      ],
      MONTHLY_MARKET_INSIGHTS: [
        {
          TITLE: 'Market Insight',
          DESCRIPTION: 'February 2026',
          PDF_ID: 'file-202602-report.pdf'
        },
        {
          TITLE: 'Market Insight',
          DESCRIPTION: 'January 2026',
          PDF_ID: 'file-202601-report.pdf'
        },
        {
          TITLE: 'Market Insight',
          DESCRIPTION: 'December 2025',
          PDF_ID: 'file-202512-report.pdf'
        }
      ],
      HIGHLIGHT:
        'Through these publications, we aim to provide not only transparency into our quantitative strategy process, but also meaningful context behind market developments. Our goal is to help you navigate the complexities of financial markets with confidence.'
    }
  },
  FOOTER_NOTE:
    'Our library resources are regularly updated to reflect the latest developments in markets and our quantitative strategies.'
};
